"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FileSpreadsheet, X } from "lucide-react"

interface Column {
  id: string
  label: string
  isVisible: boolean
  isPinned?: boolean
  width?: string
  isAlwaysVisible?: boolean
}

interface DataTableColumnToggleProps {
  columns: Column[]
  onColumnChange: (columnId: string, action: "toggle" | "pin" | "unpin") => void
  onSelectAllColumns?: (select: boolean) => void
  showTip?: boolean
  disabled?: boolean
}

// Update the DataTableColumnToggle component to better handle many columns
export function DataTableColumnToggle({ columns, onColumnChange, onSelectAllColumns, showTip = true, disabled = false }: DataTableColumnToggleProps) {
  const [open, setOpen] = useState(false)
  
  // Group columns by visibility for better organization
  const visibleColumns = columns.filter((col) => col.isVisible)
  const hiddenColumns = columns.filter((col) => !col.isVisible)

  // Check if all columns are selected or if it's an indeterminate state
  // Only count non-always-visible columns for "Select All" logic
  const nonAlwaysVisibleColumns = columns.filter((col) => !col.isAlwaysVisible)
  const visibleNonAlwaysVisibleColumns = visibleColumns.filter((col) => !col.isAlwaysVisible)
  const allColumnsSelected = nonAlwaysVisibleColumns.length === visibleNonAlwaysVisibleColumns.length && nonAlwaysVisibleColumns.length > 0
  const someColumnsSelected = visibleNonAlwaysVisibleColumns.length > 0 && visibleNonAlwaysVisibleColumns.length < nonAlwaysVisibleColumns.length

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-10 gap-2 border border-neutral-300" disabled={disabled}>
          <FileSpreadsheet className="h-4 w-4" />
          Columns ({visibleColumns.length}/{columns.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[220px] max-h-[500px] overflow-y-auto">
        {/* Header with close button */}
        <div className="flex items-center justify-between px-2 py-1.5 border-b">
          <DropdownMenuLabel className="p-0">Columns</DropdownMenuLabel>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Add Select All option */}
        <DropdownMenuCheckboxItem
          checked={allColumnsSelected}
          onCheckedChange={(checked) => onSelectAllColumns?.(!!checked)}
          onSelect={(e) => e.preventDefault()}
          className="border-b border-border font-medium"
          disabled={allColumnsSelected || nonAlwaysVisibleColumns.length === 0}
        >
          Select All Columns
        </DropdownMenuCheckboxItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Visible columns ({visibleColumns.length})</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {visibleColumns.map((column) => {
          // Disable if this is the last visible column or if it's always visible
          const isLastVisible = visibleColumns.length === 1
          const isAlwaysVisible = column.isAlwaysVisible === true
          return (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.isVisible}
            onCheckedChange={() => onColumnChange(column.id, "toggle")}
            onSelect={(e) => e.preventDefault()}
            className="capitalize"
              disabled={isLastVisible || isAlwaysVisible}
          >
            {column.label}
            {column.isPinned && <span className="ml-2 text-xs text-muted-foreground">(pinned)</span>}
          </DropdownMenuCheckboxItem>
          )
        })}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Hidden columns ({hiddenColumns.length})</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {hiddenColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.isVisible}
            onCheckedChange={() => onColumnChange(column.id, "toggle")}
            onSelect={(e) => e.preventDefault()}
            className="capitalize"
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}

        {showTip && (
          <>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Tip: Right-click on column headers for more options
        </DropdownMenuLabel>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
